import type { FieldsetHTMLAttributes } from 'react';
import React from 'react';
import type { ChildrenProp, ClassNameProp } from '../common-types';

/**
 * Props for a {@link FieldSet} component.
 */
export interface FieldSetProps
  extends ChildrenProp,
    ClassNameProp,
    Pick<
      FieldsetHTMLAttributes<HTMLFieldSetElement>,
      'name' | 'disabled' | 'form'
    > {
  /**
   * Title which can either be a string or a JSX element.
   * If it is a string, then the text will be surrounded with a `<h3>` tag.
   */
  title: string | React.JSX.Element;
}

/**
 * A simple fieldset component. Have a look at
 * {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/fieldset} if you want to know what `<fieldset>` is all about.
 *
 * @param parameters a {@link FieldSetProps} object
 * @return the created {@link React.JSX.Element}.
 * @constructor
 */
export function FieldSet({
  title,
  children,
  className = '',
  ...props
}: Readonly<FieldSetProps>): React.JSX.Element {
  const titleEl =
    typeof title === 'string' ? (
      <legend className="title">
        <h3>{title}</h3>
      </legend>
    ) : (
      title
    );

  return (
    <fieldset {...props} className={`ehs-fieldset ${className}`.trim()}>
      {titleEl}
      {children}
    </fieldset>
  );
}
